import NumberFormatOptions = Intl.NumberFormatOptions

export * from './libraries'
import {CustomAttributeDefinition, CustomAttributeType, IFilterGroupItem} from '@api/graphql'
import {IconName} from '@blueprintjs/core'
import {AssortmentCardStore, GroupByGroupStore, IC8MenuConfig, IChangeLogFieldConfig, ToggleFilterParams} from '@modules'
import {PDFTemplateName} from '@modules/pdf'
import {
  LibraryItemConfig,
  FieldRendererType,
  CatalogImageSourceConfig,
  IDataMapConfig,
  IDragType,
  IHierarchyIcon,
  ISectionConfig,
  DetailsViewsByIndex,
  ICardUIConfig,
  DetailsFieldComputedType,
  Stores,
  CatalogStore,
  IEditableCallback,
  FieldType,
  ModalSectionRows,
  DetailsFieldBackgroundColor,
  DetailsFieldType,
  BillOfMaterialsConfig,
  DetailViewHeaderFieldView,
  IFilterableGroupResult,
  DetailsFieldAssignToType,
  ICatalogUserSettings,
  IGroupOrderBy,
  IDragDropElement,
  GroupByStore,
  IGroupBySettings,
  CatalogCardImagesView,
  StoresInstance,
  LibraryItemConfigWrapper,
  CatalogSerializableSettings,
  WorkspaceElasticSearchIndex,
  Workspace
} from '@stores'
import {_, React} from '@api'
import {AssortmentLayoutSettings} from '@stores/catalog'
import {IMutationObj} from '@stores/elasticsearch/mutations'
import {ImageRatio, C8ImageSize, PageColumns} from '@utils'
import {PageSize} from '@react-pdf/types'
import {IReactionDisposer} from 'mobx'
import {WorkspaceStore} from '../../../../../services/aws/sam-create-pdf-in-s3/app/src/workspace'

export interface IDefDetails extends Exclude<CustomAttributeDefinition, { type: CustomAttributeType }> {
  type: CustomAttributeType
}

export type WorkspaceID = string

export interface WorkspaceConfig {
  createDefaultTitle?: string
  _id: string
  folderLevel?: number
  merchFolderLevel?: number
  hideWorkspaceSync?: boolean
  sections?: Array<ISectionConfig>
  createDefaultField?: string
  visible?: boolean | ((stores?: StoresInstance) => boolean)
  customImagePlaceholders?: boolean
  /**
   * Don't import attributes from linked workspaces (our fields with an esIndex)
   */
  noImportExternalAttributes?: boolean

  style?: Partial<React.CSSProperties>
  isItemEditable?: boolean | CatalogItemOperationAllowedCallback

  filterLibraries?: (library: LibraryItemConfigWrapper, stores: StoresInstance) => boolean

  /**
   * YFTS-6233
   * @param stores
   */
  reactions?: (stores: Partial<Stores>) => Array<IReactionDisposer>

  /**
   * What noun to use when ungrouped,  'product', 'merch option', etc
   */
  itemName?: string

  category?: string

  /**
   * Defaults to 'catalog'
   */
  view?: 'catalog' | 'library'

  /**
   * deprecated {@see presets}
   */
  customViews?: Array<IWorkspacePreset>

  presets?: Array<IWorkspacePreset>
  /**
   * vlp-plan or otherwise
   */
  seasonIndex?: string
  /**
   * Only Asic will be shown in top bar menu
   */
  seasonIncludedPlans?: Array<string>
  /**
   *  plan hierarchy data source
   */
  breadcrumbSource?: 'c8' | 'es'

  addHierarchyItem?: boolean

  c8Menu?: IC8MenuConfig

  seasonSelectors?: Array<SeasonSelectorConfig>

  vlp?: {
    customHierarchy?: Array<ICustomVlpHierarchy>
  }

  dataMap?: IDataMapConfig

  /**
   * What to use in the URL for this workspace
   * Maybe 'merch' instead of the title 'merchandising'
   */
  slug?: string
  title?: string
  shortTitle?: string
  libraries?: Array<LibraryItemConfig>
  catalog?: CatalogConfig
  library?: WorkspaceLibraryConfig

  showActivities?: boolean

  detailViews?: DetailsViewsByIndex

  syncIndicator?: {
    folderLevel?: number,
    syncId: string
    label: string
  }

  sidebars?: {
    left?: ISidebarConfig
    right?: ISidebarConfig
  }

  constants?: Record<string, any>
  functions?: { [fName: string]: Function }
}

export interface AssortmentDataMapItem {
  field: string,
  render?: ((data, stores: StoresInstance) => string)
  source?: 'c8' | 'cloudfront'
  transparency?: boolean

  /**
   * YFTS-9474 - workaround for sync not pulling in designer images in RH
   * The source path to FK from
   * Example: field: 'theme.attributes.rhDesigner.exId' foreignField: 'images'
   */
  foreignField?: string

  /**
   * The remote field to query image data from
   */
  esIndex?: string
}

export interface AssortmentDataMap {
  title?: AssortmentDataMapItem
  images?: string | AssortmentDataMapItem
  attributes?: Array<{ field: string, label?: string }>
  extraFields?: Array<string>
}

export type AssortmentCardAttributeConfig = string | ((data, stores: StoresInstance) => string)

export interface AssortmentSwatchConfig {
  /**
   * The field to distinct on to generate the swatches themselves
   * In RH theme.colorColorways.id
   */
  field: string,
  /**
   * The field on the actual hero items (in RH's case, the colorways in an array on a style)
   * Something like (from the colorway's perspective) - color.colorColorwayContext.exId
   */
  itemField: string,

  /**
   * Defaults to the associated workspace's item name
   */
  label?: string,
  dataMap?: AssortmentDataMap

  filter?: (data: Array<{ field: string, item: any }>, stores: StoresInstance) => Array<any>
}

export interface AssortmentConfig {
  _id?: string
  layout?: AssortmentLayoutSettings
  title?: string
  filter?: Array<any | ((stores: StoresInstance) => any)>
  grouping: Array<string>

  perspectives?: string[]
  hidePerspectiveButtons?: boolean

  dataMap?: AssortmentDataMap
  heroCards?: {
    esFilter?: { bool?: any }
    field?: string
    orderBy?: { field: string, order?: 'asc' | 'desc' }
    item?: { field: string, filter?: (data: Array<any>, stores: StoresInstance) => Array<any> }
    dataMap?: AssortmentDataMap
    sort?: (items: Array<any>, stores: StoresInstance) => Array<any>

    child?: {
      field: string
      bom?: {
        field: string,
        mustHaveField?: string

        /**
         * YFTS-9720
         */
        onlyIfField?: string

        attributes?: Array<AssortmentCardAttributeConfig>

        hideParent?: (item: any, stores: StoresInstance) => boolean
      }

      sort?: (items: Array<any>, stores: StoresInstance) => Array<any>
      images?: string | AssortmentDataMapItem

      /**
       * For RH they have attributes with stuff like 71.5", remove the .5 .0 etc
       */
      removeDecimalsInAttributes?: boolean
      attributes?: Array<AssortmentCardAttributeConfig>
    }
  }
  swatches?: Array<AssortmentSwatchConfig>
  imageBackgroundFor?: (type: 'swatch', assortment: AssortmentCardStore, stores: StoresInstance) => string | undefined

  pdf?: AssortmentPDFConfig
}

export interface AssortmentPDFConfig {
  defaultNameForAssortment?: (assortment: AssortmentCardStore) => string
}

export interface SeasonSelectorConfig {
  type: 'merch' | 'seasonal',
  fields?: Array<SeasonSelectorFieldConfig>
}

/**
 * Custom directory names for catalog eg. defaultName: "Folder 1" replaced by assignedName: "Class 1"
 */
export interface ICustomVlpHierarchy {
  assignedName?: string
  defaultName: string
  customSort?: Array<string>
}

export interface ICustomerFieldChooser {
  name: string,
  level?: number,
  field: string
}

export interface ICustomViewCatalogConfig {
  settings?: Partial<CatalogSerializableSettings>
  groupBy?: Partial<IGroupBySettings>
}

export interface ICustomViewFilterConfig {
  by?: Array<ToggleFilterParams>
}

interface ICustomViewSortConfig {
  hierarchy?: Array<{
    /**
     * The field in the hierarchy
     */
    field: string,
    /**
     * What to sort on:
     *    _key - Group Title
     *    _count - # of items
     *    other - subtitle field
     */
    sortKey?: '_count' | '_key' | string,
    order?: 'asc' | 'desc',
    custom?: boolean
  }>
  catalog?: Array<IGroupOrderBy>
}

export type CatalogFieldName = string
export type FilterLabel = string

export interface IWorkspacePreset {
  _id?: string
  default?: boolean
  label?: string,
  icon?: IconName
  divider?: boolean,

  catalog?: ICustomViewCatalogConfig & { fields?: Array<CatalogFieldName> }
  filter?: ICustomViewFilterConfig
  sort?: ICustomViewSortConfig

  // Moved directly into the catalog settings
  //merchHierarchy?: Partial<IVLPHierarchySelection> & Partial<{plan: string | Array<string>, folder: string | Array<string>, collection: string | Array<string>}>
}

export interface IFieldConfigC8 {
  serviceName?: string
  serviceCall?: string
  serviceParamName?: string
  ignoreFilter?: boolean
}

export interface SeasonSelectorFieldConfig {
  field: string
  label?: string
  hideInCatalog?: boolean
  /**
   * We generally group by name, but sometimes we want to do it by ID and have the same name show up twice - like in RH where each collection has an attribute on it
   * https://centric8.atlassian.net/browse/YFTS-9636
   */
  // idField?: string
  // keyword?: boolean
  disableOnSavingSelectionToC8?: boolean
  align?: 'left' | 'right'
  allowFilter?: boolean
  /**
   * In diff workspace, same field may have used by diff name to get the value of the current board
   * i.e. style board have seasons whereas products board have season, using this I will track value of season to get the count of style in product when season/cat1/cat2 is applied.
   */
  lookup?: string

  /**
   * In sync2 things are capital cased like Collection, Category1, etc, but in {@see C8ServicesStore} we expect the existing structure with lower case
   */
  hierarchyField?: string

  depends?: string
  dependants?: Array<string>
  c8?: IFieldConfigC8
  /**
   * Display Add item element
   */
  addItem?: boolean

  /**
   * Remove items from the seasonal filter dynamically from config
   * For example in RH, when in an assortment we want to hide collections that don't have the attribute rhProductBoardYN set - YFTS-9488
   */
  filterAvailable?: (items: Array<any>, stores: StoresInstance) => Array<any>
}

export interface ISidebarConfig {
  variant?: SidebarVariant
}

export type SidebarVariant = 'permanent' | 'persistent' | 'temporary'
export type FilterRendererType = 'range' | 'label'

export function isNumericAttribute(def?: CustomAttributeDefinition) {
  return _.includes(['Float', 'Double', 'Int'], def?.type)
}

export interface IExtraGroupByContentField {
  label?: string | ((store: GroupByGroupStore, stores: Partial<Stores>) => string),
  value?: string | ((store: GroupByGroupStore, stores: Partial<Stores>) => string),
  id?: string
}

export type WorkspaceId = string
export type SortItemsInGroupCallback = (items: Array<any>, {store, stores}: { store?: GroupByGroupStore, stores: Partial<Stores> }) => Array<any>

export type AssignValueToCatalogFieldHandler = ({field, mutations, libraryItem, stores, data, candidateOnly}: { field: ICatalogFieldConfig, mutations: { [field: string]: IMutationObj }, libraryItem: any, stores: Partial<Stores>, data?: any, candidateOnly?: boolean }) => Promise<void>

export interface ICatalogFieldConfig {
  pdf?: { hideInDefaultFilename?: boolean}
  /**
   * https://www.elastic.co/guide/en/elasticsearch/reference/current/nested.html
   * nested data type in ES, changes query syntax
   */
  esNested?: boolean

  __sanitizer?: { imported?: boolean, importChildren?: boolean, includeDuplicateIndexes?: boolean }

  /**
   * Unique key for fields in the UI - 'group by "Has Image" and sort by "Has Colorway?"
   */
  field: string

  hideWorkspaceSync?: boolean

  /**
   * Corresponding field in data document, falls back to field value if not specified
   */
  esField?: string

  /**
   * Sometimes multiple catalogs use the same underlying esIndex, distinguish them here if needed
   */
  workspace?: WorkspaceId

  /***
   * For some fields like 'hasImage' we want to see if .images OR colorways.images is set
   */
  orFields?: Array<string>

  /**
   * Sort order in places like view options, group by, filter groups, etc
   */
  order?: number

  /**
   * Usually we group by various terms, but sometimes (like with HasImage) we group by do we have a field or not on a catalog item
   */
  agg?: 'exists' | 'terms' | string

  /**
   * Within which group should this item be shown in the GUI?
   */
  section?: string

  /**
   * Skip the text from hirerachy button
   */
  skipButtonText?: string | string[]

  /**
   * Add .keyword when aggregating by this field?
   */
  keyword?: boolean
  /**
   * Group by this field?
   */
  group?: {
    /**
     * Sometimes we want to group by a unique ID like folder.id, but we want to SORT by folder.name
     */
    sortField?: string
    /**
     * Show in 'Specifications' section of group by dropdown
     */
    spec?: boolean
    title?: GroupTitleConfig
    hideSpecImage?: boolean
    subtitle?: GroupTitleConfig[]
    hideSubtitle?: boolean

    /**
     * YFTS-10982
     */
    hideSwatch?:boolean


    /**
     * YFTS-
     */
    hideGroupByColumn?: boolean
    showSwatchesWhenCollapsed?: boolean

    /**
     * *
     * For certain items (like tags) we want to group by a child path '.name.keyword'
     */
    field?: string

    /**
     * YFTS-7755 We want to group by ID but display the name attribute
     */
    labelField?: string

    add?: boolean
    /**
     * To hide Product(attributes.propduct) in custom attributes section,it should show only in specification section
     */
    customAttr?: boolean

    /**
     * To apply drag drop in group Header for a product in asics such as when groupby 'Global Adoption' is performed
     */
    allowDragDropAssign?: boolean
    /**
     * Composite queries can't be combined with pipeline filters so for certain groups like 'folder 1' we need to be able to filter the results AFTER returning from ES
     * @param key
     */
    filterResultBuckets?: (keys: Array<IFilterableGroupResult>, stores: StoresInstance) => Array<IFilterableGroupResult>

    filter?: (data: any) => boolean
    titleFor?: (data: any) => string

    cardUI?: ICardUIConfig
    groupDomAttributes?: any

    /**
     * Originally built for RH - should be moved into swatches logic TODO
     */
    subGroup?: Partial<{ field: string, filter?: (items: Array<any>) => Array<any>, customSort?: SortItemsInGroupCallback, columns?: number | ((items: Array<any>) => number) }>,

    // swatches?: Array<string | {field: string, sort?:  SortItemsInGroupCallback}>
    /**
     * render different template on PDF
     */
    pdf?: {
      template: string
    }
    /**
     * Allow for modifying data in the config
     */
    custom?: {
      showSwatchesWhenCollapsed?: boolean
      customSort?: (buckets: Array<{ key, keys } & any>, {store, stores}: { store?: GroupByGroupStore, stores: Partial<Stores> }) => any,
      formatTitle?: (title) => any,
      objectFit?: string,
      imagesFor?: (store: GroupByGroupStore, stores: Partial<Stores>) => Array<{}>
      extraHeaderCardContent?: Array<{
        position: 'inline' | 'left' | 'right',
        styles?: string,
        fields?: Array<IExtraGroupByContentField>
        rows?: Array<{
          fields: Array<IExtraGroupByContentField>
        }>
      }>
      /*
      extraHeaderCardContent: [{
                position: 'right',
                fields: [
                  { label: '004x005', css: {'data-type': 'code'}},
                  {label: 'PD_DIN_13', css: {'data-type': 'pd-code'}},
                  {label: info => `${info.title} - ${info.subtitle}`, css: {'data-type': 'collection'}},
                  {label: 'NEW', css: {'data-type': 'status'}}
                ]
              }]
       */
    }

    onAssign?: (data: any, params: { store: GroupByGroupStore, stores: Partial<Stores> }) => Promise<any>

    /**
     * Any extra data we want to get on a per group basis.  Useful for things like YFTS-7433 where we need all the merch product attributes so that when we do a D&D assign we already have them on-hand for the specific merch product in question
     */
    extraAggs?: { [aggName: string]: any }

    allowCardMode?: boolean
  }

  aggDetails?: AggregationDetailsConfig

  defaultValue?: any
  search?: boolean

  hasSelection?: boolean

  disableOnSavingSelectionToC8?: boolean

  hierarchyElements?: string[]

  /**
   * add same assignment on the given attributes when create catalogItem
   * i.e. if style is assigned to mpv same style needs to be added in styleCandidates
   */
  updateOnAssignment?: DetailsFieldAssignToType | string[]

  /**
   *Associated elastic search index to query when showing related data (info sidebar for example)
   */
  esIndex?: string

  /**
   * The field on the main catalog workspace can be something like merchProduct.name, but when we open the left library we have to query on just name
   */
  esIndexField?: string

  /**
   * Fields to include when importing/walking the foreign keys in the sanitizer.  Normally all related fields are imported and made available for filter/grouping/etc - this restricts it
   * See YFTS-9951
   */
  include?: Array<string>
  exclude?: Array<string>

  filter?: boolean | {
    subField?: string,
    renderer?: FilterRendererType
    esWhere?: Array<any>
  },

  sort?: ICatalogFieldSortConfig

  def?: CustomAttributeDefinition,
  label?: string,
  /** Ignore this field entirely - no sidebar, no card field, filter, group by, etc **/
  hidden?: boolean,
  card?: ICardFieldConfig

  // Some values have inline html that we need to be able to render
  renderer?: FieldRendererType
  rendererExtra?: any

  foreignKeys?: Array<{
    index: WorkspaceElasticSearchIndex,
    field: string
  }>

  showAsLibrary?: {
    itemStyle?: any
    unassign?: boolean,
    renderer?: 'chip' | 'card'
  } | boolean

  // hide asked attributes from the library sidebar section
  hideFromLibrarySidebar?: boolean

  /**
   * Used for season selection dropdown
   */
  depends?: string
  dependants?: Array<string>

  c8?: IFieldConfigC8

  /**
   *   Enable fetch c8 values along with modal fields i.e. perspectives and itemCreation dependents etc
   */
  fetchC8?: boolean

  /**
   * Used when a field depends on the value of another field, for example a colorway restricted to one of the associated style's colorways only (instead of the full library)
   */
  constraint?: { from: string, to: string, filterOnSidebar?: boolean }

  computed?: DetailsFieldComputedType

  onAssign?: AssignValueToCatalogFieldHandler
  /**
   * Sometimes there are multiple slots of the same type (say Color) or a given catalog item:
   *
   * Style -> Colorway -> Color
   * Style -> Colors
   *
   * This allows dragging of colors to automatically assign to Style -> Colors
   */
  assignTo?: boolean
  /**
   * In asics Product field is in attributes.asicsProduct, due to this it wasn't shown in specifications option in groupBy.
   * To show this in specification option in groupBy added showInSpecGroup flag.
   */
  showInSpecGroup?: boolean
  editable?: boolean | IEditableCallback

  mutateImportedField?: (field: ICatalogFieldConfig, parent: {field: ICatalogFieldConfig, workspace: Workspace}, stores: StoresInstance) => void

  /**
   * On dev site we have style -> colors, but also style -> colorways -> colors and want to see both
   */
  includeDuplicateIndexes?: boolean
  /**
   * In UR we need to filter colorways if they are already assigned to mvp using __assignedTo field,
   * specListFilter can contain any value that is good for elastic search filter
   */
  specListFilter?: Object

  editor?: ICatalogFieldEditorConfig

  /** For refList type fields ref fields will be used to show the view option on the ref items*/
  refField?: string

  library?: ILibraryFieldConfig

  /**
   * Special magic field used to draw the diagonal cross across objects
   *
   * Traditionally attributes.Active in Centric
   */
  ___isActiveField?: boolean
}

export interface ICatalogFieldSortConfig {
  /** Can the catalog item cards be sorted by this field? */
  catalogItem?: boolean
  custom?: {
    [sortField: string]: ICustomGroupOrder
  }
  _func?: ICatalogFieldCustomSortFn
  subField?: string
}

export type ICatalogFieldCustomSortFn = (params: {
  data?: Array<any>,
  filterItems?: Array<IFilterGroupItem>,
  stores: StoresInstance
}) => Array<string>

export interface ILibraryFieldConfig {
  hide?: boolean
}

export interface ICatalogFieldEditorConfig {
  colors?: Array<DetailsFieldBackgroundColor>
  fieldRenderer?: FieldRendererType
  editable?: IEditableCallback
  dataMap?: any
  mutateC8?: boolean
  require?: boolean
  c8Ref?: any

  /** TODO - can this be removed? **/
  baseNode?: string

  nested?: {
    /** This is relative to the parent object, not the overall catalog index row
     * as we can have something like .styles.colorways and the colorways to be shown in a nested are _for a given style instance_
     * */
    field: string
    label?: string
    dataMap?: any
  }

  // Todo - why are there two of these?
  type?: DetailsFieldType
  valueType?: FieldType

  /* {style: 'percent', minimumFractionDigits:2} */
  localeStringOptions?: NumberFormatOptions
  has3d?: boolean

  /**
   * Subfield with the 3d ID
   */
  field3d?: string
  screenshotsField?: string

  bom?: BillOfMaterialsConfig

  /**
   * Associated workspace for renderer, used by bom-details
   */
  workspace?: string

  style?: React.CSSProperties

  size?: 'small' | 'medium' | 'large'

  headerLabel?: boolean
  /**
   * Section Modal Rows in the section
   */
  rows?: ModalSectionRows

  agg?: string
  /**
   * to show only label in the section
   */
  labelOnly?: boolean

  header?: DetailViewHeaderFieldView,

  onlyUIField?: boolean

  // minimal look (true by default)
  minimal?: boolean
}

export interface AggregationDetailsConfig {
  customViews?: string[]
  agg?: '+' | '*' | 'avg' | 'sum' | string
  computed?: {
    deps?: string[]
    field?: string
    reduce?: (hits: Array<any>) => number
    compute?: (fieldValues: any) => ({
      value?: any,
      color?: string | Function
    })
  }
}

export interface ICustomGroupOrder {
  label?: string
  order: Array<string>
  orderDesc?: Array<string>
}

interface ICardFieldConfig {
  cssStyle?: Partial<React.CSSProperties>
  hide?: boolean
  /**
   * Filled in dynamically at runtime for the title label above a field value
   */
  titleFields?: string[]

  /***
   * Used when drilling down to a nested field
   */
  nestedTitleFields?: string[]

  default?: boolean
  /**
   * Fields from fk linked workspaces that you want to actually keep defaults for - YFTS-9748
   */
  defaultChildren?: Array<string>
  category?: string
  multiline?: boolean
  maxLines?: number

  /**
   * See YFTS-9581
   * The image on a card can come from a variety of sources.  When the user double-clicks a specific colorway on an mpv we open that colorway in the context of the MPV it's in
   */
  noOpenNestedImage?: boolean

  /**
   * If this field is an object and has esIndex this is assumed to be a nested card/item (a spec)
   */
  nested?: NestedCardConfig

  /**
   * Usually when a field is off another nested field (like a style on a colorway), we won't render the style on the root card if colorway AND colorway.style are checked (it'll be rendered on the colorway card which will show when moused over the colorway field).
   * Sometimes we want to render it on the top level card as well as the nested one
   * https://centric8.atlassian.net/browse/YFTS-7849
   */
  renderWhenNested?: boolean

  /**
   * If the corresponding parent.esIndex is dropped on to this card (like a style on to an mpv) then assign to THIS field when mutating the catalog item
   */
  drop?: boolean
  pdf?: boolean

  /**
   * Custom views to display this field on the card for
   */
  customViews?: string[]
}

/**
 * Sometimes a catalog item links to a spec or library field from another index, examples include
 * mpv.style
 * mpv.style.colorways
 * mpv.styleCandidates
 *
 * product.color, product.material, product.style, etc
 */
export interface NestedCardConfig {
  /**
   * Show a -> to allow for drilling down into the full field?
   */
  drilldown?: boolean

  /**
   * Show the nested item(s) as cards or inline list previews?
   */
  renderer?: 'card' | 'inline' | 'ListPreviewCard'

  showNestedPopover?: boolean

  cardDetails?: ICardUIConfig

  add?: boolean
}

export interface WorkspaceLibraryConfig {
  index: WorkspaceElasticSearchIndex
  tabs?: Array<IWorkspaceLibraryTabConfig>
  dragType?: IDragType,
  hierarchyIcons?: IHierarchyIcon[],
  features?: {
    disableGlobalSearch?: boolean,
    disableSideBar?: boolean
  }
}

export interface IWorkspaceLibraryTabConfig {
  id: string
  label?: string
  table?: {
    canCreateRow?: boolean
    canDeleteRow?: boolean
    columns?: Array<IWorkspaceLibraryTableColumnConfig>
  }
}

export interface IWorkspaceLibraryTableColumnConfig {
  field: string
  label?: string
}

export interface CatalogPerspectiveConfig {
  index?: string
  icon?: IconName

  hidden?: boolean

  itemCreation?: ItemCreationConfig

  /**
   * Used for 'New Season' in seasonal dropdown - move to seasonSelectors
   */
  itemEdit?: ItemEditConfig

  /**
   * The id of this perspective (to be added to query params)
   */
  id: string,

  field?: string,

  /** Name of the detail configuration to use when rendering the item in the sidebar */
  detailView?: string
  label: string,

  hideInHeader?: boolean

  /**
   * When we select the field and show right sidebar details, what field should be queried?
   * exId, id, _id, etc
   */
  idField?: string

  /** If keyword need to be append to search term. */
  keyword?: boolean

  // TODO change this to get the field marked as isActiveField in other workspaces
  /** If the nested object can be toggled we want to use the nested active field to indicate active/inactive **/
  activeField?: string

  /**
   * Used in https://app.zeplin.io/project/5deadde8f560844352c8baf1/screen/5ea02c9899a0c451a746a8ba
   */
  background?: string

  filter?: any

  matchCatalogItem?: (item: any, params: { stores: Partial<Stores> }) => boolean
}

export interface ICopyOverField {
  source: string
  target: string
}

export interface CatalogActionConfig {
  create?: boolean | ActionButtonsConfig
  delete?: boolean | ActionButtonsConfig
  duplicate?: boolean | ActionButtonsConfig
  toggleActive?: boolean | ToggleActiveConfig
  uploadImages?: boolean | ActionButtonsConfig
  changeColor?: boolean
  tableView?: boolean
  extraSelectionMenuItems?: Array<SelectionMenuItemConfig>
  shouldRenderControlTab?: boolean | IEditableCallback
}

export type CatalogItemOperationAllowedCallback = ({data, stores}: { data?: any, stores: Partial<Stores> }) => boolean

export interface ToggleActiveConfig {
  /**
   * Return if user is permitted to activate/de-active record depending on the business cases
   */
  isEnabled?: CatalogItemOperationAllowedCallback
}

export interface SelectionMenuItemConfig {
  text?: string
  label?: string
  icon?: IconName
  onClick?: (stores: Partial<Stores>) => void
}

export interface ActionButtonsConfig {
  /**
   * Don't create this offline, instead automatically push it to the backend
   */
  autoPublish?: boolean

  /**
   * When creating a duplicate create a reference back to the record it was cloned from
   */
  sourceUrlField?: string

  ignoreFields?: Array<{ path: string, default?: any }>

  onBefore?: ({original, clone, stores}: { original: any, clone: any, stores: Partial<Stores> }) => Promise<void>

  requireConfirm?: boolean

  /**
   * Return if user is permitted to duplicate record depending on the business cases
   */
  isEnabled?: CatalogItemOperationAllowedCallback

  /**
   * When creating a duplicate add user information to record, user data is added to path mentioned in below field
   */
  duplicatedByUser?: string
}

export interface CatalogConfig {
  defaultEditable?: boolean

  /** For toggle active/deactivate behaviors + diagonal cross **/
  activeField?: string
  reportView?: ReportViewConfig
  commonDefsDetail?: Array<IDefDetails>
  defaultImageSize?: 'smallImage' | 'thumbnailImage' | 'largeImage' | 'image'
  viewOptions?: { columns?: number }
  defaultUserSettings?: Partial<CatalogSerializableSettings>
  defaultView?: Partial<CatalogStore> & { filterConfig?: ICustomViewFilterConfig, groupBy?: Partial<GroupByStore> }
  defaultSort?: Array<{ field: string, order: 'asc' | 'desc' }>

  /**
   * ES Filter clauses to apply to ALL filters on the board
   * YFTS-6232
   */
  additionalFilterClauses?: Array<((stores: StoresInstance) => any) | any>

  /**
   * Elasticsearch index to query
   */
  index?: string
  _source: Array<string>
  _searchFields?: Array<string>

  imageSources?: Array<CatalogImageSourceConfig>

  perspectives?: Array<CatalogPerspectiveConfig>
  openInC8Field?: string
  redirectToPLM?: boolean
  fields: Array<ICatalogFieldConfig>
  //images?: IImagesPath
  tagType?: string
  initialSort?: string
  dragType?: IDragType,
  hierarchyIcons?: IHierarchyIcon[],
  sync?: ISyncType
  features?: ICatalogFeaturesConfig
  export?: WorkspaceExportConfig
  targetsToolbars?: Array<CollectionTargetsToolbarConfig>

  itemCreation?: ItemCreationConfig

  showHierarchyPlan?: boolean,

  /** Deprecated, move to cardView.presets **/
  //cardViews?: Array<PredefinedCardViewConfig>

  /**
   * Customize the {@see CardViewOptionsToolbarMenu}
   */
  cardView?: CardViewConfig

  /**
   * Assortment / Hero Card Layout
   */
  assortments?: Array<AssortmentConfig>
}

export interface CardViewConfig {
  presets?: Array<PredefinedCardViewConfig>
  /**
   * Allow the user to select all fields with a click?
   * {@default true}
   */
  allPreset?: boolean
  defaultPreset?: {
    label?: string
    show?: boolean
  }
}

export interface PredefinedCardViewConfig {
  id: string,
  label?: string,
  fields: Array<string>,
  // For checking nested dynamic fields like secondaries.attributes.VIP_ParentName.US-East
  dynamicValues?: Array<string>
  // Added by runtime to aid in looking up complex nested dynamic fields like secondaries.attributes.VIP_ParentName.SomeDynamicValue
  section?: ISectionConfig
}

interface IFeatureCardIcons {
  name: IconName
  color: string
  field: string
}

export interface LibraryFeaturesConfig {
  show?: 'all' | 'editable' | boolean
  showOtherWorkspaceFields?: boolean
}

export interface CatalogFilterToolbarSwitchConfig {
  label?: string
  field: string
  term?: any,
  /**
   * Defaults to on
   */
  defaultValue?: boolean
}


export interface ICatalogFeaturesConfig {
  createOneItemPerImage?: boolean

  /**
   * An image background to inject into CSS for things like builder cards, list preview cards, etc
   * {@see BuilderCard} {@see ListPreviewCard}
   */
  imageBackground?: string

  alwaysShowFullSizeImages?: boolean

  /**
   * YFTS-8990
   */
  noSelfLibrary?: boolean,

  /**
   * Have the image server remove white backgrounds for images on this board?
   */
  autoTransparency?: boolean

  actionButtons?: CatalogActionConfig
  maxDepthToAutoTraceFields?: number
  inlineModal?: boolean
  syncOnSave?: boolean

  library?: LibraryFeaturesConfig

  renderer?: 'loadMoreButton'

  card?: {
    cssStyles?: Record<string, string>

    cardSizes?: Record<'landscape' | 'portrait' | 'square', { size?: number, aspectRatio?: number }> & { lodMultiplier?: number }

    contextMenu?: {
      // See YFTS-8509 - we don't want the level of menu on ASICS where there are no relationships
      noCardMenuItems?: boolean
    }
    objectFit?: 'cover' | 'contain'

    /**
     * https://centric8.atlassian.net/browse/YFTS-10201
     */
    maxStackImages?: number

    /**
     * Overrides --hero-card-inner-image-padding
     */
    heroImagePadding?: string

    /**
     * Overrides --card-image-padding
     */
    imagePadding?: string


    /**
     * Overrides --card-image-align-self
     */
    alignSelf?: 'flex-start' | 'flex-end' | 'center'

    fullWidth?: boolean

    /**
     * For EF the borders on expanded cards are ugly due to irregular images
     */
    noBorders?: boolean

    /**
     *
     */
    drilldown?: {},
    icons?: Array<IFeatureCardIcons>
  },
  stackedCards?: boolean,
  /**
   * YFTS-8562
   */
  toolbarSwitches?: Array<CatalogFilterToolbarSwitchConfig>,
  groupBy?: {
    /**
     * Enable things like columns, full width, full height, full height, single-line cells
     */

    /**
     * Puts a label above each individual group
     */
    showGroupLabel?: boolean

    allowInlineDetailAggregations?: boolean
    detailAggregations?: Array<GroupDetailAggregationConfig>

    dropdown?: {
      rows?: number
      columns?: number
    }
  }
  details?: {
    variant?: SidebarVariant
    //modal?: IModalDetailsConfig
  }

  showHierarchyPlan?: boolean

  imageSources?: { multiple?: boolean, default?: { ids: Array<string>, view?: CatalogCardImagesView } }

  sectionOverrides?: Record<string, Partial<ISectionConfig>>

  changelog?: ChangeLogCatalogWorkspaceConfig
}

export interface ChangeLogCatalogWorkspaceConfig {
  /**
   * For example when viewing changes in the colorways space, we want to show [Style Object] -> [Colorway Object] in the log
   */
  parents?: Array<string>
}

export interface ItemCreationConfig {
  required?: string[]
  clone?: string[]
  depends?: string[]
  copyOverFields?: ICopyOverField[]
  hierarchy?: boolean
  defaultFieldValues?: IField[]
}

export interface IField {
  field: String
  value: any | ((stores: StoresInstance) => void)
}

export interface ItemEditConfig {
  editable?: Array<IEditableFields>
}

export interface IEditableFields {
  field?: string
  type?: string
}

export interface CollectionTargetsToolbarConfig {
  /**
   * ES Filter to run, often to only select active items
   */
  filter?: any
  /**
   * Fields that must be set in the catalog before displaying the component
   * Usually collection + collectionVersion
   */
  requirements?: string[]

  /**
   * Rows to be displayed in the collection targets components
   */
  targets: Array<CollectionTargetToolbarConfig>

  columns: Array<CollectionTargetColumnConfig>

}

export interface CollectionTargetColumnConfig {
  type: 'text' | 'targets'
  label?: string
  field?: string
}

export interface CollectionTargetToolbarConfig {
  id: string
  label?: string
  hidden?: boolean
  prefix?: string
  suffix?: string
  precision?: number
  computed?: {
    deps?: string[]
    compute?: (deps: any) => number
    reduce?: (hits: Array<any>) => number
  }
  agg?: 'sum' | 'count' | 'max' | 'min' | string
  field?: string
}

export interface ISyncType {
  default: Array<string>
  showOnlyDefault: boolean
  noVersionSync: boolean
  showVersions: boolean
}

export interface IExportOption {
  name?: string,
  value?: string,
  default?: boolean
}

interface IExportModal {
  modalTitle?: string
  exportOptions?: Array<IExportOption>
}

export interface IExportableField {
  field: string,
  label: string,
  type?: string,
  precision?: number,
  subfield?: string
}

interface IExportImage {
  include?: boolean,
  height?: number,
  width?: number,
  fields?: string[],
  label?: string,
  type?: string
}

export interface IExportHelperParams {
  image?: IExportImage,
  defaultExportFields?: Array<IExportableField>,
  staticExportFields?: Array<IExportableField>,
  nameFields?: Array<string>
  selectedImageConfig?: IExportOption
}

export interface WorkspaceExportItemConfig {
  enabled?: boolean
  exportType: string
  filePrefix?: string
  modal?: IExportModal
  helperParams?: IExportHelperParams
}

export interface WorkspaceExportConfig {
  excel?: WorkspaceExportItemConfig
  images?: WorkspaceExportItemConfig
}

export interface GroupTitleConfig {
  field?: string
  prefix?: string
  missing_bucket?: boolean
  valueFor?: (data) => { item: any, label: string }
}

export interface WorkspaceChangeLogConfig {
  additional?: Array<IChangeLogFieldConfig>
  title?: (item: any, field: string, stores) => string
  fields: Array<string>
  exportFields?: Array<IExportableField>
}

export type WorkspaceChangeLogAdditionalConfigType = {
  type?: 'datetime' | 'workspace' | string
  label?: string
  workspace?: string
  workspaceLabel?: string
  index?: string
  values?: Array<any>
  value?: { id: string, name?: string }
  nested?: boolean
}

export interface GroupDetailAggregationConfig {
  inlineColumns?: number
  filter?: any
  sections: Array<GroupDetailAggregationSectionConfig>
  aggregationDeps?: Array<string>
  aggregationFilterField?: string
  defaultSelectedLabel?: string
  // showShopList?:boolean
  // selection?: GroupDetailAggregationSelection
}

export interface GroupDetailAggregationSectionConfig {
  label?: string
  fields: Array<GroupDetailAggregationFieldConfig>
  inline?: boolean
}

export interface GroupDetailAggregationSelection {
  id: string
  // items: Array<GroupDetailAggregationSelectionItem>
}

export interface GroupDetailAggregationSelectionItem {
  id: string
  label: string
  // fields: Array<GroupDetailAggregationSelectionField>
}

export interface GroupDetailAggregationSelectionField {
  esField: string
  field: string
}

export interface GroupDetailAggregationFieldConfig {
  label?: string
  name?: string
  field?: string
  output?: string // output field if we are using the same input field repeatedly
  aggFn?: any
  agg?: '+' | '*' | 'avg' | 'sum' | 'max' | 'min' | string | any
  /**
   * If set to false, will be hidden in inline mode
   */
  inline?: boolean
  filter?: (selectedShopValues: any, allShopValues?: any) => any | any
  assortmentFilter?: () => any,
  hidden?: boolean
  def?: CustomAttributeDefinition
  format?: (v) => string
  prefix?: string
  dataPath?: string
  computed?: {
    deps: string[]
    field?: string
    reduce?: (hits: Array<any>, selectedShopValues: any) => number
    compute?: (fieldValues: any) => any
    color?: Function
    flag?: ({value}) => boolean
  }
  /**
   * If the field is on a nested data type you either need to specify the path to it here or have a corresponding catalog field with the associated nested section data
   */
  /* As per the latest development,
   * Mention the nestedPath only if its being used in some kind of aggregation query Bug: YFTS-6890
   */
  nestedPath?: string,

  /*
    UR uses inner aggregration for nested path and asics need sum aggregation without inner query
  */
  isInnerAgg?: boolean

  valueField?: string
}

export interface VIPDropEventArgs {
  drag?: IDragDropElement
  drop?: IDragDropElement
  context?: any
  dataTransfer?: DataTransfer
}

export enum FieldTypes {
  collectionVersion = 'collectionVersion'
}

export interface PdfExportConfig {
  imageSize: C8ImageSize;
  columns: PageColumns;
  imageRatio: ImageRatio;
  selectedTemplate?: string
  selectedSection?: PdfConfigSection
  queryLimit?: number
  document?: Array<PdfSizeDocument>
  group?: PdfGroupStyleConfig
  layout?: 'column' | 'row' | string
  productPage: { fontFamily: { [key: string]: string } }
  reRenderOnResize?: boolean
  sections: {
    // Cover page?
    cover?: boolean
    specGlossary?: boolean,
    products?: boolean
  }
  customAttributes: {
    [key: string]: boolean
  }
  groupByCustomAttributes: {
    [key: string]: boolean
  }
  watermark?: boolean
  showLogo?: boolean
  showFilters?: boolean
  showTotalCount?: boolean
  showCount?: boolean
  showHrGroupingLines?: boolean
  showVtGroupingLines?: boolean
  header?: {
    showGroupBy?: boolean;
    show?: boolean,
    text?: string
    image?: string
  },
  footer?: {
    show?: boolean,
    showPage?: boolean
    text?: string
    image?: string
  },
  logos?: Array<PdfLogo>
}

interface PdfGroupStyleConfig {
  spec?: PdfStyleConfig
  catalogItem?: PdfStyleConfig
}

interface PdfStyleConfig {
  style?: any
}

export type PdfOrientation = 'landscape' | 'portrait'

export type PdfConfigSection = 'document' | 'header' | 'footer' | string

export interface PdfLogo {
  name: string,
  path: string
}

export interface PdfPageProps {
  summaryTitle?: React.ReactNode
  summarySubtitle?: React.ReactNode
  pageTitle?: React.ReactNode
  item?: any
  itemType?: 'spec'
  hideTitle?: boolean
  size?: string,
  orientation?: PdfOrientation
}

export interface PdfSizeDocument {
  size?: PageSize,
  pageHeight?: number
  orientation?: PdfOrientation
  title?: string,
  subtitle?: string,
  subject?: string,
  author?: string
  brand?: string
}

export interface PdfLocalInfo {
  document?: Array<PdfSizeDocument>,
  config?: Partial<PdfExportConfig>,
  selectedLogo?: string
}

interface IReportViewColumnConfig {
  id?: string
  label?: string
  format?: (v) => string
  agg?: any
}

export interface ReportViewConfig {
  valueColumns?: Array<IReportViewColumnConfig>
}



// WEBPACK FOOTER //
// ./src/stores/config/deployment/workspace/index.ts